<template>
    <div
        v-if="data"
        class="ai-counter-card"
        :style="cardStyle"
        data-testid="ai-counter-card"
    >
        <div class="ai-counter-card__header">
            <div class="ai-counter-card__icon rounded-full shrink-0">
                <WidgetStatusIcon
                    :icon="CARD_ICON"
                    color="transparent"
                    :size="finalIconSize"
                />
            </div>
            <span
                class="flex-1 truncate font-medium select-none"
                :style="{ fontSize: `${layoutConfig.titleFontSize * 1.3}px` }"
            >{{ cardText }}</span>
        </div>

        <div
            class="ai-counter-card__body"
        >
            <div
                class="ai-counter-card__content"
                :class="contentClasses"
            >
                <div
                    class="ai-counter-card__value"
                    :style="{ fontSize: `${layoutConfig.valueFontSize}px` }"
                >
                    {{ cardValue }}
                </div>

                <div
                    v-if="cardSubtitle"
                    class="ai-counter-card__desrc"
                    :style="{ fontSize: `${layoutConfig.titleFontSize}px` }"
                >
                    {{ cardSubtitle }}
                </div>
            </div>
        </div>

        <div class="ai-counter-card__footer">
            <RedesignTimeFrame 
                :widget="data"
                style="font-size: 16px;"
            />
        </div>

        <RedesignBaseSpinner
            v-if="isLoading"
            class="absolute right-1 bottom-0"
        />
    </div>
</template>

<script setup lang="ts">
import WidgetStatusIcon from '@/components/RedesignComponents/Cards/WidgetStatusIcon.vue'
import RedesignBaseSpinner from '@/components/RedesignBaseComponents/RedesignBaseSpinner.vue'
import RedesignTimeFrame from '@/components/RedesignComponents/RedesignTableComponents/RedesignTimeFrame.vue'
import { WidgetExtended } from '@/types/dashboard'
import { computed } from 'vue'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { ObjectAnyType } from '@/types/generic'
import { getWidgetLayoutColors } from '@/components/RedesignComponents/Widgets/widget.helper'
import {
    COUNTER_CARD_LAYOUT_COLUMNS_VALUE,
    COUNTER_CARD_LAYOUT_DEFAULT_VALUE,
    WIDGET_CONTENT_ALIGNMENT,
} from '@/enum/default-layout-config'
import {
    AGGREGATE_BY_PARAMETER_NAME,
    QUESTIONS_PARAMETER_NAME
} from '@/enum/widgetTemplateConfigs'

function humanize (text: string): string {
    const normalized = text.replace(/_/g, ' ')
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase()
}

const CARD_ICON = 'IconSparkles'
const DEFAULT_TITLE_FONT_SIZE = 14
const DEFAULT_FONT_SIZE = 47
const DEFAULT_VALUE_ICON_SIZE = 32
const ICON_OFFSET = 12

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
        showText?: boolean
        displayBorder?: boolean
        queue?: boolean
    }>(),
    {
        showText: true,
        displayBorder: true,
    })

/* Composable */
const {
    data: widgetData,
    isLoading
} = useWidgetDataWithRefreshInterval(props, 'data')

/* Computed */
const widgetLayout = computed(() => props.data.WidgetLayout ?? {})

const layoutConfig = computed(() => ({
    titleFontSize: widgetLayout.value.titleFontSize || DEFAULT_TITLE_FONT_SIZE,
    valueFontSize: widgetLayout.value.valueFontSize || DEFAULT_FONT_SIZE,
    valueIconMinWidth: widgetLayout.value.valueIconMinWidth || DEFAULT_VALUE_ICON_SIZE,
}))

const finalIconSize = computed(() => {
    const baseSize = layoutConfig.value.valueIconMinWidth
    return Math.max(baseSize - ICON_OFFSET, 0)
})

const contentClasses = computed(() => {
    const align = widgetLayout.value.contentAlignment || WIDGET_CONTENT_ALIGNMENT
    const isColumn = (widgetLayout.value.contentLayout || COUNTER_CARD_LAYOUT_DEFAULT_VALUE) === COUNTER_CARD_LAYOUT_COLUMNS_VALUE

    return [
        isColumn ? 'layout-column' : 'layout-default',
        isColumn ? `text-${align} items-${align}` : `text-${align} justify-${align}`,
    ]
})

const cardText = computed(() => props.data.Title)
const widgetColors = computed(() => getWidgetLayoutColors(props.data))

const cardValue = computed(() => {
    const resData = (widgetData.value || []) as Array<ObjectAnyType>
    const resValue = resData[0] || {}
    const firstKey = Object.keys(resValue)[0]
    return resValue[firstKey] || 0
})

const cardSubtitle = computed(() => {
    const toLabel = (parameterName: string) => {
        const config = props.data.WidgetConfig?.find(
            item => item.ParameterName?.toLowerCase() === parameterName.toLowerCase()
        )

        if (!config?.WidgetParameterValue) return ''

        const value = config.WidgetParameterValue
        const listLabel = config.ParameterData?.ItemList?.find(
            (item: { Key: string; Value: string | number }) => String(item.Value) === value
        )?.Key

        return humanize(listLabel ?? value)
    }

    return `${toLabel(AGGREGATE_BY_PARAMETER_NAME)}. ${toLabel(QUESTIONS_PARAMETER_NAME)}`.trim()
})

const cardStyle = computed(() => ({
    '--ai-counter-border-color': widgetColors.value.frames,
}))
</script>

<style lang="scss" scoped>
.ai-counter-card {
    @apply w-full h-full flex flex-col px-3;

    &__header {
        @apply flex items-center gap-2 py-2 overflow-hidden;
        width: calc(100% - 22px);
        color: var(--colorWidgetHeaderText-color);
    }

    &__icon {
        background: linear-gradient(143.82deg, #3C53FF 11.35%, #34D7F8 100%);
    }

    &__body {
        @apply flex-1 min-h-0 flex items-center w-full overflow-hidden;
    }

    &__content {
        @apply flex leading-none w-full;

        &.layout-default {
            @apply flex-row flex-wrap items-end gap-2;
        }

        &.layout-column {
            @apply flex-col gap-1;
        }
    }

    &__footer {
        @apply py-2.5 leading-none border-t-2;
        border-color: var(--ai-counter-border-color, var(--gray-400));
        color: var(--dark-gray);
        font-size: 16px;
    }

    &__title,
    &__value {
        @apply font-medium uppercase;
    }

    &__value {
        font-variant-numeric: tabular-nums;
        line-height: 0.75
    }

    &__desrc {
        color: var((--colorWidgetTimeText-color), var(--dark-gray));
    }
}
</style>
